/* eslint-disable @typescript-eslint/no-explicit-any */
import { ChangeEvent } from 'react';
import { types, Instance, flow } from 'mobx-state-tree';
import { LogModel, ILogModel, LogsInfoModel, ILogsInfoModel } from './models/log.model';
import { DateRangeModel, IDateRangeModel } from './models/dateRange.model';
import { DateRange } from 'materialui-daterange-picker';
import { AxiosResponse } from 'axios';
import axios from 'server';
import { filterGroupsList } from './filterGropsList';
import { capitalizeFirstLetter } from 'utils';
import { todayStart, todayEnd } from 'components/DateRangePicker/DefaultRanges';
import { AdminModel, IAdminModel } from '../AdminsTable/models/admin.model';
import { LDAPGroupModel, ILDAPGroupModel } from 'domain/ldap/models/ldapGroup.model';
import { LDAPDepartmentModel, ILDAPDepartmentModel } from 'domain/ldap/models/ldapDepartment.model';

const { model, array, string, number, maybeNull } = types;

const normalizeDateRange = (startDate: Date, endDate: Date) => {
  if (startDate > endDate) {
    return { startDate: endDate, endDate: startDate };
  }

  return { startDate, endDate };
};

export const LogsTableStore = model('LogsTableStore', {
  logs: array(LogModel),
  filter: string,
  groupFilter: array(string),
  clickedId: number,
  dateRangeFilter: DateRangeModel,
  logsInfo: maybeNull(LogsInfoModel),
  admins: maybeNull(AdminModel),
  groups: maybeNull(LDAPGroupModel),
  departments: maybeNull(LDAPDepartmentModel),
})
  .views((self) => ({
    get additionalData(): string | undefined {
      const log = self.logs.find(({ id }) => id === self.clickedId);
      if (!log?.additionalData) {
        return undefined;
      }

      return log?.additionalData.replace(/{|}|"/gm, '').replace(/:/g, ': ').replace(/,/g, '\n');
    },
    get logsView(): ILogModel[] {
      let startDate = new Date(self.dateRangeFilter.startDate);
      let endDate = new Date(self.dateRangeFilter.endDate);
      if (startDate > endDate) {
        [startDate, endDate] = [endDate, startDate];
      }
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(23, 59, 59, 999);
      const logsInDateRange = self.logs.slice().filter(({ createdAt }) => {
        if (!createdAt) return false;
        const logDate = new Date(createdAt);
        const isInRange = logDate >= startDate && logDate <= endDate;
        return isInRange;
      });
      // Only apply group filter if groupFilter has items and they're not 'all'
      const groupedLogs =
        self.groupFilter.length > 0 && !self.groupFilter.includes('all')
          ? logsInDateRange.filter(({ type }) => {
              const isIncluded = self.groupFilter.findIndex((group) => group === type) > -1;
              return isIncluded;
            })
          : logsInDateRange;

      if (!self.filter || self.filter.trim() === '') {
        return groupedLogs;
      }

      const searchTerm = self.filter.toLowerCase().trim();
      const filtered = groupedLogs.filter(({ ip, email, description, createdAt, clientEmail }) => {
        const searchableText = `${ip || ''} ${email || ''} ${clientEmail || ''} ${description || ''} ${
          createdAt || ''
        }`.toLowerCase();
        const matches = searchableText.includes(searchTerm);
        return matches;
      });
      return filtered;
    },
    getRenderValueForGroupSelect(): string {
      const renderValues = self.groupFilter.map((group) => `${capitalizeFirstLetter(group)}s`);
      return renderValues.join(', ');
    },
    get showAllDateRange(): IDateRangeModel {
      const firstLog = self.logs.length > 0 ? self.logs[0] : null;
      const lastLog = self.logs.length > 0 ? self.logs[self.logs.length - 1] : null;
      const startDate = firstLog && firstLog.createdAt ? new Date(firstLog.createdAt) : todayStart;
      const endDate = lastLog && lastLog.createdAt ? new Date(lastLog.createdAt) : todayEnd;

      return normalizeDateRange(startDate, endDate);
    },
    get logsDateRangeDescription(): string {
      if (self.dateRangeFilter.startDate.toDateString() === self.dateRangeFilter.endDate.toDateString()) {
        return ` (${self.dateRangeFilter.startDate.toDateString()})`;
      }
      return ` (from ${self.dateRangeFilter.startDate.toDateString()} to ${self.dateRangeFilter.endDate.toDateString()})`;
    },
  }))
  .volatile(() => ({}))
  .actions((self) => ({
    normalizeLogs({ data }: AxiosResponse): { logs: ILogModel[]; meta: any } {
      // Transform API response to match LogModel structure
      const rawLogs = Array.isArray(data.data?.data)
        ? data.data.data
        : Array.isArray(data.data)
        ? data.data
        : Array.isArray(data)
        ? data
        : [];
      const logsData = rawLogs.map((log: any) => ({
        id: log.id,
        ip: log.properties?.ip || log.properties?.ip_address || null,
        email: log.properties?.email || log.causer?.email || null,
        clientEmail: log.properties?.clientEmail || null,
        description: log.description || null,
        type: log.log_name || log.properties?.type || null,
        additionalData: JSON.stringify(log.properties || {}) || null,
        createdAt: log.created_at || null,
        eventType: log.event || null,
        clientId: log.properties?.client_id || null,
        city: log.properties?.city || null,
        country: log.properties?.country || null,
        zip_code: log.properties?.zip_code || null,
        created_by: log.properties?.created_by || null,
        subject_id: log.subject_id || null,
      }));

      self.logs.replace(logsData);
      self.groupFilter.replace(filterGroupsList);

      if (logsData.length > 0) {
        self.dateRangeFilter = normalizeDateRange(
          new Date(logsData[0]?.createdAt || todayStart),
          new Date(logsData[logsData.length - 1]?.createdAt || todayEnd)
        );
      }

      // Extract pagination metadata
      const meta = {
        total: data.data?.total || data.total || logsData.length,
        per_page: data.data?.per_page || data.per_page || 10,
        current_page: data.data?.current_page || data.current_page || 1,
        last_page: data.data?.last_page || data.last_page || 1,
      };

      return { logs: self.logs, meta };
    },
    normalizeLogsInfo({ data }: AxiosResponse): ILogsInfoModel | null {
      self.logsInfo = data.data;
      return self.logsInfo;
    },
    normalizeAdmins({ data }: AxiosResponse): IAdminModel | null {
      self.admins = data.data;
      localStorage.setItem('selectedAdmin', JSON.stringify(self.admins));
      return self.admins;
    },
    normalizeGroups({ data }: AxiosResponse): ILDAPGroupModel | null {
      localStorage.setItem('CurrentGroup', JSON.stringify(data.data));
      return data.data;
    },
    normalizeDepartments({ data }: AxiosResponse): ILDAPDepartmentModel | null {
      localStorage.setItem('CurrentDepartment', JSON.stringify(data.data));
      return data.data;
    },
    setFilter(filter: string) {
      self.filter = filter;
    },
    clearStore(): ILogModel[] {
      self.logs.clear();
      return self.logs;
    },
    setGroupFilter(event: ChangeEvent<{ value: unknown }>): void {
      self.groupFilter.replace(event.target.value as string[]);
    },
    setClickedId(clickedId: number): void {
      self.clickedId = clickedId;
    },
    setDateRangeFilter({ startDate, endDate }: DateRange): void {
      const normalizedRange = normalizeDateRange(startDate as Date, endDate as Date);

      self.dateRangeFilter.startDate = normalizedRange.startDate;
      self.dateRangeFilter.endDate = normalizedRange.endDate;
    },
  }))
  .actions((self) => ({
    getAdminLogs: flow(function* (page = 1, limit = 10, search = '') {
      try {
        const params: any = {
          type: 'admin',
          limit,
          page,
        };
        if (search && search.trim() !== '') {
          params.search = search.trim();
        }
        const response = yield axios.get('logs', { params });
        return self.normalizeLogs(response);
      } catch (error: any) {
        throw new Error(error);
      }
    }),

    getEndLogs: flow(function* (page = 1, limit = 10, search = '') {
      try {
        const params: any = { page, limit };
        if (search && search.trim() !== '') {
          params.search = search.trim();
        }
        const response = yield axios.get<ILogModel[]>('/user/logs', { params });
        const result = self.normalizeLogs(response.data);
        return { ...result, risk: response.data?.data?.risk ?? response.data?.risk ?? null };
      } catch (error: any) {
        throw new Error(error?.message || 'Something went wrong');
      }
    }),
    getCloudLdapLogs: flow(function* (page = 1, limit = 10, search = '') {
      try {
        const params: any = {
          type: 'ldap',
          limit,
          page,
        };
        if (search && search.trim() !== '') {
          params.search = search.trim();
        }
        const response = yield axios.get(`logs`, { params });
        return self.normalizeLogs(response);
      } catch (error: any) {
        throw new Error(error);
      }
    }),
    getAppsLogs: flow(function* (page = 1, limit = 10, search = '') {
      try {
        const params: any = {
          type: 'app_connections',
          limit,
          page,
        };
        if (search && search.trim() !== '') {
          params.search = search.trim();
        }
        const response = yield axios.get(`logs`, { params });
        return self.normalizeLogs(response);
      } catch (error: any) {
        throw new Error(error);
      }
    }),
    getAttributeLogs: flow(function* (page = 1, limit = 10, search = '') {
      try {
        const params: any = {
          type: 'attribute_mapping',
          limit,
          page,
        };
        if (search && search.trim() !== '') {
          params.search = search.trim();
        }
        const response = yield axios.get(`logs`, { params });
        return self.normalizeLogs(response);
      } catch (error: any) {
        throw new Error(error);
      }
    }),
    getUserSyncLogs: flow(function* (page = 1, limit = 10, search = '') {
      try {
        const params: any = {
          type: 'user_sync',
          limit,
          page,
        };
        if (search && search.trim() !== '') {
          params.search = search.trim();
        }
        const response = yield axios.get(`logs`, { params });
        return self.normalizeLogs(response);
      } catch (error: any) {
        throw new Error(error);
      }
    }),
    getLogsInfo: flow(function* (id: number) {
      const result = yield axios
        .get<ILogsInfoModel>(`logs/detail/${id}`)
        .then(self.normalizeLogsInfo)
        .catch((error) => {
          throw new Error(error);
        });
      return result;
    }),
    getEndUserLogs: flow(function* (page = 1, limit = 10, search = '') {
      try {
        const params: any = {
          type: 'ldap',
          limit,
          page,
        };
        if (search && search.trim() !== '') {
          params.search = search.trim();
        }
        const response = yield axios.get(`logs`, { params });
        return self.normalizeLogs(response);
      } catch (error: any) {
        throw new Error(error?.message || 'Failed to fetch logs');
      }
    }),

    viewUser: flow(function* (id: number) {
      const result = yield axios
        .get<any>(`ldap/user/${id}`)
        .then((res) => res)
        .catch((error) => {
          throw error.response.data;
        });
      return result;
    }),
    viewEndUser: flow(function* (id: number) {
      const hostname = window.location.hostname;
      const parts = hostname.split('.');
      const domain = parts.length > 1 && hostname !== 'localhost' ? parts[0] : '';
      const result = yield axios
        .get<any>(`user/groups-departments/${id}?domain=${domain}`)
        .then((res) => res)
        .catch((error) => {
          throw error.response.data;
        });
      return result;
    }),
    getAdmins: flow(function* (id: number) {
      const result = yield axios
        .get<IAdminModel>(`admin/admins/${id}`)
        .then(self.normalizeAdmins)
        .catch((error) => {
          throw new Error(error.response.data.data.message);
        });
      return result;
    }),
    getGroups: flow(function* (dn) {
      if (!dn || dn.trim() === '') {
        throw new Error('The group is already deleted.');
      }
      const result = yield axios
        .get<ILDAPGroupModel>(`admin/ldap/group/${dn}`)
        .then(self.normalizeGroups)
        .catch((error) => {
          throw new Error(error.response.data.data.message);
        });

      return result;
    }),
    getlogGroups: flow(function* (id) {
      const result = yield axios
        .get<ILDAPGroupModel>(`groups/${id}`)
        .then(self.normalizeGroups)
        .catch((error) => {
          throw new Error(error.response.data.data.message);
        });
      return result;
    }),
    getDepartments: flow(function* (dn) {
      if (!dn || dn.trim() === '') {
        throw new Error('The department is already deleted.');
      }
      const result = yield axios
        .get<ILDAPDepartmentModel>(`admin/ldap/department/${dn}`)
        .then(self.normalizeDepartments)
        .catch((error) => {
          throw new Error(error.response.data.data.message);
        });
      return result;
    }),
    getlogDepartment: flow(function* (id) {
      const result = yield axios
        .get<ILDAPDepartmentModel>(`departments/${id}`)
        .then(self.normalizeDepartments)
        .catch((error) => {
          throw new Error(error.response.data.data.message);
        });
      return result;
    }),
    clearLogs: flow(function* () {
      const result = yield axios
        .post('admin/logs/delete-all')
        .then(self.clearStore)
        .catch((error) => {
          throw new Error(error);
        });
      return result;
    }),

    getOidcReports: flow(function* (period: string, startDate: string, endDate: string) {
      try {
        const params = {
          period,
          start_date: startDate,
          end_date: endDate,
        };
        const response = yield axios.get('logs/oidc-reports', { params });
        return response.data;
      } catch (error: any) {
        throw new Error(error?.response?.data?.message || 'Failed to fetch API reports');
      }
    }),

    getSamlReports: flow(function* (period: string) {
      try {
        const params = {
          period,
        };
        const response = yield axios.get('logs/saml-reports', { params });
        return response.data;
      } catch (error: any) {
        throw new Error(error?.response?.data?.message || 'Failed to fetch API reports');
      }
    }),

    getapiReports: flow(function* (startDate: string, endDate: string) {
      try {
        const params = {
          start_date: startDate,
          end_date: endDate,
        };
        const response = yield axios.get('logs/api-reports', { params });
        return response.data;
      } catch (error: any) {
        throw new Error(error?.response?.data?.message || 'Failed to fetch OIDC reports');
      }
    }),
    getManagers: flow(function* () {
      try {
        const response = yield axios.get('logs/managers');
        return response.data;
      } catch (error: any) {
        throw new Error(error?.response?.data?.message || 'Failed to fetch managers');
      }
    }),

    getMangerReports: flow(function* (managerId?: number | string) {
      try {
        const params = managerId ? { manager_id: managerId } : undefined;
        const response = yield axios.get('logs/manager-report-data', { params });
        return response.data;
      } catch (error: any) {
        throw new Error(error?.response?.data?.message || 'Failed to fetch Manager reports');
      }
    }),
  }));

export type ILogsTableStore = Instance<typeof LogsTableStore>;
